import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';

import { ReactiveFormsModule, FormBuilder, Validators, FormGroup } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { CompanyService, Company } from '../../../services/company.service';
import { VoucherProductService } from '../../../services/voucher-product.service';
import { Subject, takeUntil, filter } from 'rxjs';
import { SaveButtonComponent } from '../../save-button/save-button.component';
import { EmbedPreviewComponent } from '../../embed-preview/embed-preview.component';
import { LoggerService } from '../../../services/logger.service';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideChevronRight,
  lucideCode,
  lucideInfo,
  lucidePackage,
  lucidePlus,
  lucideX,
} from '@ng-icons/lucide';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmTextareaImports } from '@spartan-ng/helm/textarea';
import { HlmSwitchImports } from '@spartan-ng/helm/switch';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';

/** Mirrors MAX_FIXED_VOUCHER_AMOUNTS and MINIMUM_VOUCHER_AMOUNT_FLOOR in the backend. */
const MAX_FIXED_AMOUNTS = 6;
const AMOUNT_FLOOR_EUROS = 2;

@Component({
  selector: 'app-sell-settings',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    SaveButtonComponent,
    EmbedPreviewComponent,
    NgIcon,
    HlmCardImports,
    HlmItemImports,
    HlmSeparatorImports,
    HlmTypographyImports,
    HlmFieldImports,
    HlmInputImports,
    HlmTextareaImports,
    HlmSwitchImports,
    HlmBadgeImports,
    HlmButtonImports,
  ],
  providers: [
    provideIcons({
      lucideChevronRight,
      lucideCode,
      lucideInfo,
      lucidePackage,
      lucidePlus,
      lucideX,
    }),
  ],
  templateUrl: './sell-settings.component.html',
  styleUrls: ['./sell-settings.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SellSettingsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private initialFormValues: any;
  loading = false;
  formChanged = false;
  company: Company | null = null;
  hasProducts = false;
  readonly maxFixedAmounts = MAX_FIXED_AMOUNTS;
  newFixedAmount = '';
  fixedAmountError = '';

  @ViewChild('saveButton') saveButton!: SaveButtonComponent;

  sellForm: FormGroup;

  constructor(
    private companyService: CompanyService,
    private voucherProductService: VoucherProductService,
    private fb: FormBuilder,
    private logger: LoggerService,
  ) {
    this.sellForm = this.fb.group({
      voucher_app_title: ['', Validators.required],
      voucher_app_description: ['', Validators.required],
      minimum_voucher_amount: [
        '',
        [Validators.required, Validators.min(2), Validators.pattern(/^\d+$/)],
      ],
      money_voucher_enabled: [true],
      fixed_voucher_amounts_enabled: [false],
      fixed_voucher_amounts: [[] as number[]], // cents, ascending
      voucher_products_enabled: [false],
    });

    this.sellForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.checkFormChanged());
  }

  ngOnInit() {
    this.companyService.company$
      .pipe(
        takeUntil(this.destroy$),
        filter((c) => c !== null),
      )
      .subscribe((company) => {
        this.company = company;
        this.populateForm(company);
      });

    this.voucherProductService.products$.pipe(takeUntil(this.destroy$)).subscribe((products) => {
      this.hasProducts = products.length > 0;
    });
  }

  private checkFormChanged() {
    if (this.initialFormValues) {
      // JSON, so the amounts list compares by content
      this.formChanged = !Object.keys(this.initialFormValues).every(
        (key) =>
          JSON.stringify(this.initialFormValues[key]) ===
          JSON.stringify(this.sellForm.get(key)?.value),
      );
    }
  }

  private populateForm(company: Company) {
    this.sellForm.patchValue(
      {
        voucher_app_title: company.voucher_app_title || '',
        voucher_app_description: company.voucher_app_description || '',
        minimum_voucher_amount: company.minimum_voucher_amount
          ? company.minimum_voucher_amount / 100
          : 10,
        money_voucher_enabled:
          company.money_voucher_enabled !== undefined ? company.money_voucher_enabled : true,
        fixed_voucher_amounts_enabled: company.fixed_voucher_amounts_enabled || false,
        fixed_voucher_amounts: company.fixed_voucher_amounts || [],
        voucher_products_enabled: company.voucher_products_enabled || false,
      },
      { emitEvent: false },
    );
    this.initialFormValues = this.sellForm.value;
    this.formChanged = false;
  }

  /** Field errors show once the field has been touched. */
  isInvalid(controlName: string): boolean {
    const control = this.sellForm.get(controlName);
    return !!control?.invalid && !!control?.touched;
  }

  get minimumAmountError(): string {
    const errors = this.sellForm.get('minimum_voucher_amount')?.errors;
    if (!errors) return '';
    if (errors['required']) return 'Mindest-Gutscheinbetrag ist erforderlich';
    if (errors['min']) return 'Mindestens 2 €';
    return 'Bitte eine ganze Zahl eingeben';
  }

  get fixedAmounts(): number[] {
    return this.sellForm.get('fixed_voucher_amounts')?.value || [];
  }

  /** The fixed-amount card is on but has nothing to sell: saving is blocked. */
  get fixedAmountsMissing(): boolean {
    return !!this.sellForm.get('fixed_voucher_amounts_enabled')?.value && !this.fixedAmounts.length;
  }

  /** Adds the typed amount (euros, comma or dot) to the list. */
  addFixedAmount() {
    const euros = Number(this.newFixedAmount.trim().replace(',', '.'));
    const cents = Math.round(euros * 100);
    if (!this.newFixedAmount.trim() || !Number.isFinite(euros) || cents / 100 !== euros) {
      this.fixedAmountError = 'Bitte einen Betrag in Euro eingeben, z. B. 25 oder 25,50';
    } else if (euros < AMOUNT_FLOOR_EUROS) {
      this.fixedAmountError = `Mindestens ${AMOUNT_FLOOR_EUROS} €`;
    } else if (this.fixedAmounts.includes(cents)) {
      this.fixedAmountError = 'Diesen Betrag gibt es schon';
    } else if (this.fixedAmounts.length >= MAX_FIXED_AMOUNTS) {
      this.fixedAmountError = `Höchstens ${MAX_FIXED_AMOUNTS} Beträge`;
    } else {
      this.setFixedAmounts([...this.fixedAmounts, cents]);
      this.newFixedAmount = '';
      this.fixedAmountError = '';
    }
  }

  removeFixedAmount(cents: number) {
    this.setFixedAmounts(this.fixedAmounts.filter((amount) => amount !== cents));
  }

  private setFixedAmounts(amounts: number[]) {
    const control = this.sellForm.get('fixed_voucher_amounts');
    control?.setValue([...amounts].sort((a, b) => a - b));
    control?.markAsDirty();
  }

  formatEuros(cents: number): string {
    return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  }

  onSubmit() {
    if (!this.sellForm.valid) return;

    const moneyVoucherEnabled = this.sellForm.get('money_voucher_enabled')?.value;
    const fixedAmountsEnabled = this.sellForm.get('fixed_voucher_amounts_enabled')?.value;
    const productVouchersEnabled = this.sellForm.get('voucher_products_enabled')?.value;

    if (this.fixedAmountsMissing) {
      this.saveButton.showErrorMessage('Bitte gib mindestens einen festen Betrag an.');
      return;
    }

    if (!moneyVoucherEnabled && !fixedAmountsEnabled && !productVouchersEnabled) {
      this.saveButton.showErrorMessage('Mindestens eine Gutschein-Art muss aktiviert sein.');
      return;
    }

    this.loading = true;
    this.saveButton.hideMessages();

    const formData = { ...this.sellForm.value };
    if (formData.minimum_voucher_amount) {
      formData.minimum_voucher_amount = Math.round(formData.minimum_voucher_amount * 100);
    }

    this.companyService.updateCompanyDetails(formData).subscribe({
      next: () => {
        this.loading = false;
        this.initialFormValues = this.sellForm.value;
        this.formChanged = false;
        this.saveButton.showSuccessMessage();

        const voucherWidgets = document.querySelectorAll('schenkwerk-voucher');
        voucherWidgets.forEach((widget) => widget.dispatchEvent(new CustomEvent('reload-widget')));
      },
      error: (error) => {
        const errorMessage =
          error.error?.detail || 'Fehler beim Speichern. Bitte versuche es erneut.';
        this.logger.error('Error saving sell settings:', error);
        this.loading = false;
        this.saveButton.showErrorMessage(errorMessage);
      },
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
